/**
 * Thumbnails of the marked screenshots in the recent dictations anywhere.
 * Each mounted list keeps its own cache: an image is read from Rust once per
 * `historyId:index` and kept as a Blob URL while its record is shown, so the
 * history refetch that runs several times per dictation re-reads nothing
 * already loaded. Records no longer shown, reads that land after their record
 * went, and everything on unmount are revoked. A failed read leaves no
 * thumbnail and is tried again on the next refetch.
 */
import { useEffect, useState } from "react";
import { readDictationAnywhereImage } from "@/desktop/dictation-anywhere/dictation-anywhere-native";

export interface DictationThumbnail {
  /** Position of the image in the record, as Rust numbers it. */
  index: number;
  url: string;
}

interface ImageRecord {
  id: string;
  image_count: number;
}

interface ThumbnailCache {
  /** historyId → index → Blob URL. */
  loaded: Map<string, Map<number, string>>;
  /** historyId → index → token of the read in flight; others are stale. */
  reading: Map<string, Map<number, object>>;
}

const createCache = (): ThumbnailCache => ({
  loaded: new Map(),
  reading: new Map(),
});

async function readThumbnail(
  historyId: string,
  index: number,
): Promise<string | null> {
  try {
    const bytes = await readDictationAnywhereImage(historyId, index);
    return URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
  } catch {
    return null;
  }
}

/** Forgets every record not in `keep`, revoking its URLs. */
function prune(cache: ThumbnailCache, keep: ReadonlySet<string>) {
  for (const [historyId, urls] of cache.loaded) {
    if (keep.has(historyId)) continue;
    for (const url of urls.values()) URL.revokeObjectURL(url);
    cache.loaded.delete(historyId);
  }
  for (const historyId of cache.reading.keys())
    if (!keep.has(historyId)) cache.reading.delete(historyId);
}

function read(
  cache: ThumbnailCache,
  historyId: string,
  index: number,
  onLoaded: () => void,
) {
  const token = {};
  const inFlight = cache.reading.get(historyId) ?? new Map<number, object>();
  cache.reading.set(historyId, inFlight.set(index, token));
  void readThumbnail(historyId, index).then((url) => {
    const current = cache.reading.get(historyId);
    if (current?.get(index) !== token) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    current.delete(index);
    if (!url) return;
    const urls = cache.loaded.get(historyId) ?? new Map<number, string>();
    cache.loaded.set(historyId, urls.set(index, url));
    onLoaded();
  });
}

/** The loaded thumbnails of each shown record, in image order. */
export function useDictationThumbnails(
  records: readonly ImageRecord[],
): (historyId: string) => DictationThumbnail[] {
  const [cache] = useState(createCache);
  const [, setVersion] = useState(0);
  useEffect(() => {
    prune(cache, new Set(records.map((record) => record.id)));
    const onLoaded = () => setVersion((version) => version + 1);
    for (const { id, image_count } of records)
      for (let index = 0; index < image_count; index += 1)
        if (
          !cache.loaded.get(id)?.has(index) &&
          !cache.reading.get(id)?.has(index)
        )
          read(cache, id, index, onLoaded);
  }, [cache, records]);
  useEffect(() => () => prune(cache, new Set()), [cache]);
  return (historyId) =>
    [...(cache.loaded.get(historyId) ?? [])]
      .sort(([a], [b]) => a - b)
      .map(([index, url]) => ({ index, url }));
}
